import { Loader } from '@openx/components-core/lib/Loader/Loader';
import type { ContentObjectOption, ContentObjectProps, IntersectFormState } from '@openx/types/targeting/contentObject';
import type { OptionItem, UseOptionFetch } from '@openx/types/targeting/targeting';
import type { UseContentObjectOptionsConfig } from '@openx/types/targeting/targetingHookTypes';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../../utils/context/TargetingContext';
import { EmptyFilterMessage } from '../../../shared/EmptyFilterMessage/EmptyFilterMessage';
import { FiltersTitle } from '../../../shared/FiltersTitle/FiltersTitle';
import { SelectedItems } from '../../../shared/SelectedItems/SelectedItems';
import { Switch } from '../../../shared/Switch/Switch';
import type { TargetingItemsProps } from '../../../types';
import { itemTitle } from '../../constants';

export const IntersectOptionsItems = (
  props: TargetingItemsProps<ContentObjectProps> & {
    type: ContentObjectOption;
  }
) => {
  const { useContentObjectOptionsConfig } = useTargetingContext();

  if (!useContentObjectOptionsConfig) {
    throw new Error('useContentObjectOptionsConfig is not provided');
  }

  return <IntersectOptionsItemsHooksRender {...props} useContentObjectOptionsConfig={useContentObjectOptionsConfig} />;
};

const IntersectOptionsItemsHooksRender = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  type,
  disabledReasonMessage,
  useContentObjectOptionsConfig,
}: TargetingItemsProps<ContentObjectProps> & {
  type: ContentObjectOption;
  useContentObjectOptionsConfig: UseContentObjectOptionsConfig;
}) => {
  const props = { onChange, readonly, targetingParams, type, disabledReasonMessage };
  const optionsConfig = useContentObjectOptionsConfig();
  const { useOptionFetch, ...optionsGroup } = optionsConfig;

  const optionItem = optionsGroup[type];

  if (optionItem === true) {
    return <IntersectFetchOptionsItemsRender useOptionFetch={useOptionFetch} {...props} />;
  }

  return <IntersectOptionsItemsRender optionItem={optionItem} {...props} />;
};

const IntersectFetchOptionsItemsRender = ({
  useOptionFetch,
  ...props
}: TargetingItemsProps<ContentObjectProps> & {
  type: ContentObjectOption;
  useOptionFetch: UseOptionFetch;
}) => {
  const optionItem = useOptionFetch(props.type);
  return <IntersectOptionsItemsRender optionItem={optionItem} {...props} />;
};

const IntersectOptionsItemsRender = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  type,
  disabledReasonMessage,
  optionItem,
}: TargetingItemsProps<ContentObjectProps> & {
  type: ContentObjectOption;
  optionItem: OptionItem;
}) => {
  const { t } = useTranslation();

  const { options, loading } = optionItem;

  const { op, val } = (targetingParams[type] as IntersectFormState) || {
    op: Intersect.INTERSECTS,
    val: new Set<string>(),
  };

  const handleChange = useCallback(
    (values: string[]) => {
      const selected = new Set(values);
      const returnValue = values.length === 0 ? null : { op, val: selected };

      onChange((prevState: ContentObjectProps) => {
        return {
          ...prevState,
          [type]: returnValue,
        };
      });
    },
    [onChange, op, type]
  );

  const selectedItems = useMemo(() => [...val], [val]);

  if (loading) {
    return <Loader />;
  }

  if (val.size === 0) {
    return <EmptyFilterMessage text={t('No filters applied.')} />;
  }

  const typeLabel = op === Intersect.NOT_INTERSECTS ? t('blocked') : t('allowed');
  const filtersTitle = val.size ? typeLabel : '';
  const switchState = op === Intersect.NOT_INTERSECTS;

  return (
    <div data-test="sub-section">
      <FiltersTitle
        onClear={() => handleChange([])}
        title={itemTitle(type, val.size)}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />

      {!readonly && (
        <Switch
          switchState={switchState}
          onSwitchChange={() =>
            onChange(prevState => ({
              ...prevState,
              [type]: {
                op: switchState ? Intersect.INTERSECTS : Intersect.NOT_INTERSECTS,
                val,
              },
            }))
          }
          switchOffLabel={t('allow')}
          switchOnLabel={t('block')}
          groupTitle={t('the following:')}
        />
      )}

      <SelectedItems
        filtersTitle={filtersTitle}
        isAddIcon={!switchState}
        selectedItems={selectedItems}
        readonly={readonly}
        handleChange={handleChange}
        options={options}
        dataTest={type}
      />
    </div>
  );
};
